import { computed, watch, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useMediaQuery } from '@vueuse/core';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';

/**
 * Computes which viewer modes should be available, combining server-side
 * state (logs availability via the artifacts store) with client-side state
 * (viewport is mobile). Also handles automatic Video-mode fallback when the
 * viewport changes from desktop to mobile while Logs mode is active.
 *
 * Summary mode is intentionally excluded until the text focus layout ships.
 *
 * Usage: call once at the top of a layout component.
 */
export const useViewerAvailableModes = (): {
  availableModes: ComputedRef<ViewerMode[]>;
  isMobile: ComputedRef<boolean>;
} => {
  const viewerModeStore = useViewerModeStore();
  const { currentMode } = storeToRefs(viewerModeStore);
  const { insightsURL } = storeToRefs(useArtifactsStore());

  const isMobile = useMediaQuery('(max-width: 1023px)');
  const hasLogs = computed(() => insightsURL.value !== '');

  const availableModes = computed<ViewerMode[]>(() => {
    const modes: ViewerMode[] = [ViewerMode.VIDEO];
    if (hasLogs.value && !isMobile.value) {
      modes.push(ViewerMode.LOGS);
    }
    return modes;
  });

  // If the current mode becomes unavailable (e.g. user resizes to mobile
  // while in Logs mode, or the artifact disappears), fall back to Video.
  watch(
    availableModes,
    modes => {
      if (!modes.includes(currentMode.value)) {
        viewerModeStore.setMode(ViewerMode.VIDEO);
      }
    },
    { immediate: true }
  );

  return { availableModes, isMobile: computed(() => isMobile.value) };
};
